import React from "react";
import { baseURLImage, tryFormatDetailDateTime } from "Utils/utils";
import { formatAuditFieldName } from "./auditFieldLabels";
import useCareSector from "hooks/useCareSector";

const DiffView = ({ changes }) => {
  const { careSector } = useCareSector();
  if (!changes || Object.keys(changes).length === 0) return null;

  const isUrlLike = (value) => {
    if (typeof value !== "string") return false;
    return (
      value.startsWith("http://") ||
      value.startsWith("https://") ||
      value.startsWith("s3://") ||
      value.startsWith("/sp/") ||
      value.startsWith("sp/")
    );
  };

  const getFileNameFromPath = (value) => {
    if (typeof value !== "string" || !value.trim()) return "";
    const noQuery = value.split("?")[0].split("#")[0];
    const normalized = noQuery.replace(/\\/g, "/");
    const parts = normalized.split("/").filter(Boolean);
    return parts.length ? parts[parts.length - 1] : noQuery;
  };

  const isImageName = (fileName) =>
    /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(fileName || "");

  const isImageField = (field) =>
    /image|photo|picture|avatar|profile_picture|signature|sign|pt_sign/i.test(
      field || ""
    );

  const toDisplayUrl = (val) => {
    if (typeof val !== "string") return "";
    if (/^https?:\/\//i.test(val)) return val;
    if (val.startsWith("s3://")) return "";
    return baseURLImage(val.replace(/^\/+/, ""));
  };

  const parseBoolish = (val) => {
    if (typeof val === "boolean") return val;
    if (typeof val === "number") return val !== 0;
    if (typeof val === "string") {
      const v = val.trim().toLowerCase();
      if (["1", "true", "yes", "y"].includes(v)) return true;
      if (["0", "false", "no", "n", ""].includes(v)) return false;
    }
    return null;
  };

  const formatValue = (val, field) => {
    if (val === null || val === undefined)
      return <span className="text-gray-400 italic">empty</span>;
    const dateFormatted = tryFormatDetailDateTime(val, field);
    if (dateFormatted !== null) return dateFormatted;
    if (field === "shift_accept") {
      // DB field meaning: true => acceptance required (not accepted yet), false => accepted.
      // UI meaning here should be "Shift Accepted": invert the stored flag.
      const boolVal = parseBoolish(val);
      if (boolVal === null) return String(val);
      return boolVal ? "No" : "Yes";
    }
    if (typeof val === "boolean") return val ? "Yes" : "No";
    if (typeof val === "object") return JSON.stringify(val);
    if (isUrlLike(val)) {
      const fileName = getFileNameFromPath(val) || "file";
      const displayUrl = toDisplayUrl(val);
      const canOpen = /^https?:\/\//i.test(displayUrl);
      const isImage = isImageName(fileName) || isImageField(field);

      if (canOpen && isImage) {
        return (
          <a href={displayUrl} target="_blank" rel="noreferrer" title={fileName}>
            <img
              src={displayUrl}
              alt={fileName}
              className="h-16 w-16 rounded border object-cover"
              loading="lazy"
            />
          </a>
        );
      }

      return canOpen ? (
        <a
          href={displayUrl}
          target="_blank"
          rel="noreferrer"
          className="text-blue-600 underline break-all"
        >
          {fileName}
        </a>
      ) : (
        <span className="break-all">{fileName}</span>
      );
    }
    return String(val);
  };

  return (
    <div className="mt-2 border rounded-lg overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Field
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              Old Value
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
              New Value
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Object.entries(changes).map(([field, vals]) => (
            <tr key={field}>
              <td className="px-3 py-2 text-sm font-medium text-gray-700">
                {formatAuditFieldName(field, careSector)}
              </td>
              <td className="px-3 py-2 text-sm text-red-600 line-through">
                {formatValue(vals?.old, field)}
              </td>
              <td className="px-3 py-2 text-sm text-green-600">
                {formatValue(vals?.new, field)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DiffView;
